import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  fetchShares,
  createShare,
  destroyShare,
  acceptShare,
  declineShare,
} from './sharesAPI'

import { selectAuthToken } from '../user/userSlice'

const initialState = {
  isFetching: false,
  didValidate: false,
  lastUpdated: null,
  allIds: [],
  byIds: {},
}

const EMPTY_SHARE_IDS = []

// Every share I am part of. /shares.json sends a flat array, each one
// {id, page_id, page_title, owner_login, is_mine, guest_id, guest_login, state,
// created_at, updated_at, url}.
//
// `is_mine` is whether the page is mine, so it splits the shares I gave from the
// ones that name me, and the two can never overlap: nobody can share a page with
// themselves. `state` is 'pending', 'accepted' or 'declined'.
//
// This runs on load, not because the user asked for it, so a failure is quiet:
// the store stays empty and nothing is flashed. An unverified user gets a 401
// here (SharesController runs authenticate_verified_user!), and a flash would
// mean a mystery alert on every page load. The pages fetch is quiet for the same
// reason. Creating and deleting a share do report their failures, because there
// the user is waiting for an answer.
export const fetchSharesAsync = () => async (dispatch, getState) => {
  dispatch(sharesFetching())
  const result = await fetchShares(dispatch)

  if (result.failure || !result.response.ok) {
    console.error('Could not load shares:', result.failure || result.response.status)
    dispatch(sharesFetchFailed())
    return result
  }

  dispatch(loadSharesData(result.json))
  return result
}

// Shares a page with someone named by login or email address.
//
// These two keep the store right and say nothing: they don't flash, they return
// the `deviseFetch` result and leave every message to the caller. The share UI is
// a modal dialog, and a flash raised while it is open sits behind the backdrop
// where it can't be read, so the dialog shows its own messages. A caller that
// isn't modal can pass the result to `showFailedRequest`.
//
// A 201 means it worked, and the store already holds the new share. A 422 carries
// `{errors: {guest_login_or_email: [...]}}` for the form to show under its field.
export const createShareAsync =
  ({ pageId, guestLoginOrEmail }) =>
  async (dispatch, getState) => {
    const authToken = selectAuthToken(getState())
    const result = await createShare(dispatch, authToken, {
      page_id: pageId,
      guest_login_or_email: guestLoginOrEmail,
    })

    if (!result.failure && result.response.status === 201) {
      dispatch(addShare(result.json))
    }

    return result
  }

// Stops sharing a page with one person. The server answers 204 with no body.
export const destroyShareAsync = (shareId) => async (dispatch, getState) => {
  const authToken = selectAuthToken(getState())
  const result = await destroyShare(dispatch, authToken, shareId)

  if (!result.failure && result.response.status === 204) {
    dispatch(removeShare(shareId))
  }

  return result
}

// Answering an offer, which only the guest a share names may do. Both answer 200
// with the share in its new state, which goes straight into the store.
//
// Like the two above, these say nothing themselves and return the result for the
// caller to report. The caller also has to refetch the pages, because an answer
// changes which pages I can see and this slice knows nothing about pages: see
// `ShareOffers.jsx`. Importing `fetchPagesAsync` here would add another cycle to
// the module graph (pages already reaches shares through the user slice), and
// that is not worth saving a line at the call site.
export const acceptShareAsync = (shareId) => async (dispatch, getState) => {
  const authToken = selectAuthToken(getState())
  const result = await acceptShare(dispatch, authToken, shareId)

  if (!result.failure && result.response.ok) {
    dispatch(addShare(result.json))
  }

  return result
}

export const declineShareAsync = (shareId) => async (dispatch, getState) => {
  const authToken = selectAuthToken(getState())
  const result = await declineShare(dispatch, authToken, shareId)

  if (!result.failure && result.response.ok) {
    dispatch(addShare(result.json))
  }

  return result
}

export const sharesSlice = createSlice({
  name: 'shares',
  initialState,
  reducers: {
    sharesFetching: (state) => {
      state.isFetching = true
    },
    sharesFetchFailed: (state) => {
      state.isFetching = false
    },
    // The payload is what /shares.json sends: a flat array of shares.
    loadSharesData: (state, action) => {
      const shares = action.payload || []

      state.allIds = []
      state.byIds = {}

      shares.forEach((share) => {
        state.allIds.push(share.id)
        state.byIds[share.id] = share
      })

      state.isFetching = false
      state.didValidate = true
      state.lastUpdated = Date.now()
    },
    // The payload is one share, as /shares.json returns it on a create.
    addShare: (state, action) => {
      const share = action.payload
      if (!share || typeof share.id === 'undefined') {
        return
      }

      if (!state.allIds.includes(share.id)) {
        state.allIds.unshift(share.id)
      }
      state.byIds[share.id] = share
      state.lastUpdated = Date.now()
    },
    removeShare: (state, action) => {
      const shareId = action.payload
      const idx = state.allIds.indexOf(shareId)
      if (idx !== -1) {
        state.allIds.splice(idx, 1)
      }
      delete state.byIds[shareId]
      state.lastUpdated = Date.now()
    },
    clearSharesData: () => ({
      ...initialState,
      allIds: null,
      byIds: null,
    }),
  },
  extraReducers: (builder) => { },
})

export const {
  sharesFetching,
  sharesFetchFailed,
  loadSharesData,
  addShare,
  removeShare,
  clearSharesData,
} = sharesSlice.actions

export default sharesSlice.reducer

export const selectAllShares = (state) => state.shares?.allIds || EMPTY_SHARE_IDS

export const selectShare = (shareId) => (state) => state.shares?.byIds?.[shareId]

export const selectSharesForPage = (pageId) =>
  createSelector(
    [(state) => state.shares.allIds, (state) => state.shares.byIds],
    (allIds, byIds) => {
      const safeByIds = byIds || {}
      return (allIds || []).filter((shareId) => safeByIds[shareId]?.page_id === pageId)
    }
  )

// The other side of the store: the shares that name me, which someone else made.
// `is_mine === false` rather than falsy, so an old record without the field (the
// store survives a stale fetch) doesn't turn up here as someone else's.
const selectSharesToMe = createSelector(
  [(state) => state.shares.allIds, (state) => state.shares.byIds],
  (allIds, byIds) => {
    const safeByIds = byIds || {}
    return (allIds || [])
      .map((shareId) => safeByIds[shareId])
      .filter((share) => share?.is_mine === false)
  }
)

// The offers waiting for an answer, oldest first, which is the order they were
// made in. These are what the drawer shows above my pages: a pending share is in
// no other response, since it keeps the page out of /pages.json.
export const selectPendingSharesToMe = createSelector(
  [selectSharesToMe],
  (shares) =>
    shares
      .filter((share) => share.state === 'pending')
      .slice()
      .reverse()
)

// My own share of a page someone shared with me, whatever state it is in, so the
// page's entry can offer to remove it. Nobody can have two shares of one page
// with the same guest (the server makes `[page, guest]` unique), so there is at
// most one.
export const selectShareToMeForPage = (pageId) =>
  createSelector([selectSharesToMe], (shares) =>
    shares.find((share) => share.page_id === pageId)
  )

// The shares on a page as whole records, for a list that names its guests.
// Memoised, so a component gets the same array back until the shares change.
export const selectShareRecordsForPage = (pageId) =>
  createSelector(
    [(state) => state.shares.allIds, (state) => state.shares.byIds],
    (allIds, byIds) => {
      const safeByIds = byIds || {}
      return (allIds || [])
        .map((shareId) => safeByIds[shareId])
        .filter((share) => share?.page_id === pageId)
    }
  )
